import { useTouchRipple } from "@/components/touch-ripple";
import { Checkbox } from "@/components/ui/checkbox";
import { MuiCheckBoxIcon, MuiCheckBoxOutlineBlankIcon } from "@/components/ui/mui-checkbox-icons";
import { cn } from "@/lib/utils";

// A checkbox with its label beside it, drawn the way the ported screens'
// checkboxes are drawn.
//
// The look is MUI's `Checkbox size='small'` inside a `FormControlLabel`, and
// the theme overrides neither component, so MUI's defaults are the source spec:
// a 20px box checked in `primary.main` (#003c77), and `FormControlLabel`'s 11px
// negative margin so the *label* lines up with the text above it.
//
// **Two densities, because the donor draws two.** Both use the same 20px box;
// they differ in the box's hit-box padding and in the label's typography, and
// both sets of numbers are measured off the running donor:
//
//   - `"default"` — the item edit page's Internal Use Only pair. MUI's own
//     defaults: a 9px pad around the box (a 38px hit box) under a `body1` 16px
//     label.
//   - `"compact"` — the items filter sidebar, which passes `body2`: a 5px pad
//     under a 14px label.
//   - `"caption"` — the Spec list column filter's "Keep current selection
//     when searching" (`ColumnFilterPopover.tsx`): `sx={{ p: 0.5 }}` on the
//     box, a 4px pad, inside a `FormControlLabel sx={{ ml: 0, gap: 0.5 }}` —
//     so no negative margin and 4px between the pad and a `caption` label
//     (12px at 1.66). The donor writes `color='text.secondary'` on that
//     caption, but the running donor draws it in `text.primary`, and the
//     running donor is the spec.
//
// **The pad is a button, and it shows it.** MUI's `Checkbox` is a `ButtonBase`
// under a `SwitchBase` root at `padding: 9; borderRadius: '50%'`
// (`internal/SwitchBase.js:38-39`), so the pad is a circle — and with the
// default `color='primary'` and `disableRipple: false` it paints
// `alpha(primary.main, action.hoverOpacity)` on hover and runs the ripple on
// press (`Checkbox/Checkbox.js:69-78`). The donor theme overrides eight
// components and `MuiCheckbox` is not one of them, so those defaults are the
// spec: a 38px navy-4% disc behind the box under the pointer, over
// `currentColor` — which MUI takes from the box's own ink, `text.secondary`
// unchecked and `primary.main` checked.
//
// **The pad belongs to the box, not to the row.** MUI puts it on the `Checkbox`
// itself, so a default row is 38px tall — the hit box — and the 24px label line
// inside it changes nothing. Padding the *label* instead pads the text as well,
// which makes the same row 42 and drops everything below it by four pixels.
//
// The default is MUI's default, so a new caller that says nothing gets the
// control MUI would have given it, and the sidebar names its narrower choice.
//
// **`size="medium"` is MUI's unsized `Checkbox`** — a 24px icon inside the
// same 9px pad, a 42px hit box — for a donor that passes no `size` at all: the
// Create Visualization dialog's option rows (#755). It draws MUI's own
// `CheckBoxOutlineBlank` / `CheckBox` glyphs (`mui-checkbox-icons.tsx`) over a
// control that paints nothing itself, since the vendored primitive's stroked
// tick on a bordered box is not the filled square with the tick cut out that
// the donor shows. Its density there is `"cell"`: the box at MUI's 9px pad
// and the label at `fontSize: 'inherit'` (the donor's `slotProps.typography`),
// so it takes the table cell's 14px while keeping `body1`'s 1.5 line height.
//
// Shared rather than page-local for the reason the migration guidelines give:
// a component the donor shares is a component Atlas shares. It wraps the
// vendored shadcn `checkbox.tsx` rather than editing it (`CLAUDE.md`,
// "Conventions").

interface CheckboxFieldProps {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  /**
   * Which side of the box the label sits on — MUI's `labelPlacement`.
   *
   * `"end"` (the default) is the filter sidebar's; `"start"` is the item edit
   * page's Internal Use Only pair, where the donor passes
   * `labelPlacement='start'`. It is not a mirror of the same control: MUI swaps
   * the row *and* the negative margin, so the 11px that pulls the label flush
   * on one moves to the other side and a 16px margin appears opposite it. Both
   * sets of numbers are measured off the running donor.
   */
  labelPlacement?: "start" | "end";
  /**
   * How tightly the control is drawn — see the two densities above.
   *
   * `"default"` is MUI's own (9px pad, 16px label); `"compact"` is the filter
   * sidebar's (5px pad, 14px label); `"caption"` the column filter's (4px pad,
   * no pull, 12px label).
   */
  density?: "default" | "compact" | "caption" | "cell";
  /** The box: MUI's `size='small'` (20px, the default here) or its unsized 24px. */
  size?: "small" | "medium";
  /** Out of use — drawn faded, with the pointer no longer offering a click. */
  disabled?: boolean;
  className?: string;
  /**
   * The box's own classes, where the donor passes `sx` to the `Checkbox`
   * rather than to its label — the line menu's `sx={{ mr: 2 }}` (#810).
   */
  boxClassName?: string;
  "data-testid"?: string;
}

export function CheckboxField({
  label,
  checked,
  onChange,
  labelPlacement = "end",
  density = "default",
  size = "small",
  disabled = false,
  className,
  boxClassName,
  "data-testid": testId,
}: CheckboxFieldProps) {
  // `SwitchBase` passes `centerRipple` (`internal/SwitchBase.js:198`): the wash
  // starts at the middle of the disc however the box was pressed.
  const { rippleTargetProps, rippleLayer } = useTouchRipple({ center: true, disabled });

  return (
    <label
      className={cn(
        "flex w-fit items-center",
        density === "default"
          ? "text-base leading-6"
          : density === "compact"
            ? "text-sm leading-[1.43]"
            : density === "cell"
              ? "text-[length:inherit] leading-normal"
              : "gap-1 text-xs leading-[1.66]",
        // `text.disabled` on the label, restated rather than faded — MUI never
        // drops the opacity of a disabled control, it changes its colours.
        disabled ? "text-lennar-ink-disabled cursor-not-allowed" : "text-lennar-ink cursor-pointer",
        // `FormControlLabel`'s own margins, which differ per placement rather
        // than mirroring: `margin: 0 0 0 -11px` with the label after the box,
        // and `margin: 0 -11px 0 16px` with it before.
        labelPlacement === "start"
          ? "-mr-[11px] ml-4 flex-row-reverse"
          : density !== "caption" && "-ml-[11px]",
        className,
      )}
      data-testid={testId}
    >
      {/* The `Checkbox`'s own padding — its hit box, the whole of the gap
        between the box and the label, and the disc that paints under the
        pointer. A `gap` on the row would be a second one. */}
      <span
        {...(disabled ? {} : rippleTargetProps)}
        className={cn(
          "relative flex rounded-full",
          density === "default" || density === "cell"
            ? "p-[9px]"
            : density === "compact"
              ? "p-[5px]"
              : "p-1",
          // `currentColor` is what the ripple washes in, and this span holds no
          // text — so the box's own ink is set here and reaches nothing else.
          disabled
            ? "text-lennar-action-disabled"
            : cn(
                "hover:bg-lennar-primary-hover",
                checked ? "text-lennar-blue-600" : "text-lennar-ink-secondary",
              ),
          boxClassName,
        )}
      >
        <Checkbox
          checked={checked}
          disabled={disabled}
          // Radix reports `"indeterminate"` for a third state this control never
          // enters; `=== true` keeps that from arriving as a truthy string.
          onCheckedChange={(next) => onChange(next === true)}
          // MUI's small checkbox is a 20px square with a 2px rule, checked in
          // primary.main rather than shadcn's smaller, tinted default; out of
          // use it is `action.disabled`, unchecked and uncheckable.
          // **15px drawn inside a 20px slot.** `LabeledCheckBox` passes
          // `iconFontSize='small'` (`labeled-checkbox.tsx:13,16-17`), so the
          // icon is 20px (`SvgIcon.js:71`) — but MUI's box glyph is only 18 of
          // its 24 viewBox units (`CheckBoxOutlineBlank.js`: the outer path
          // runs 3,3 → 21,21), which is 15px drawn. The slot is what the row
          // measures, so the margin keeps it at 20 and nothing below moves;
          // the border and the corner scale with the square, 2 units of 24
          // apiece.
          className={
            size === "medium"
              ? // The glyph beside it is the picture; the control keeps only
                // its box, its focus ring and its semantics.
                "m-0 size-6 shrink-0 rounded-none border-0 bg-transparent shadow-none disabled:opacity-100 data-[state=checked]:border-0 data-[state=checked]:bg-transparent [&>[data-slot=checkbox-indicator]]:hidden"
              : cn(
                  "m-[2.5px] size-[15px] shrink-0 rounded-[1.67px] border-[1.67px] shadow-none [&_svg]:size-3",
                  "border-lennar-ink-secondary data-[state=checked]:border-lennar-blue-600 data-[state=checked]:bg-lennar-blue-600",
                  "disabled:border-lennar-action-disabled disabled:data-[state=checked]:border-lennar-action-disabled disabled:data-[state=checked]:bg-lennar-action-disabled disabled:opacity-100",
                )
          }
        />
        {size === "medium" ? (
          // In the pad, over the control, in the pad's own ink — and not in
          // the pointer's way, so a click still lands on the control.
          <span aria-hidden className="pointer-events-none absolute inset-[9px] flex">
            {checked ? <MuiCheckBoxIcon /> : <MuiCheckBoxOutlineBlankIcon />}
          </span>
        ) : null}
        {rippleLayer}
      </span>
      <span>{label}</span>
    </label>
  );
}
